import { useEffect, useState } from 'react';
import { api, type ClaimStatus, type User } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { describePasskeyError, passkeysSupported, signInWithPasskey } from '../lib/passkey';
import { Claim } from './Claim';

type Fallback = 'none' | 'totp' | 'recovery';

export function SignIn() {
  const { refresh } = useAuth();
  const [claim, setClaim] = useState<ClaimStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallback, setFallback] = useState<Fallback>('none');
  const [showClaim, setShowClaim] = useState(false);

  useEffect(() => {
    api
      .get<ClaimStatus>('/api/auth/claim')
      .then((status) => {
        setClaim(status);
        // Only take over the page when there is nobody to sign in as. With one
        // account already created, signing in has to stay the primary path or
        // the first person is locked out until the second one claims.
        setShowClaim(status.remaining === 2);
      })
      .catch(() => setClaim({ open: false, remaining: 0 }));
  }, []);

  if (claim === null) {
    return <main className="center" aria-busy="true" />;
  }

  if (showClaim) {
    return (
      <Claim
        remaining={claim.remaining}
        onCancel={claim.remaining < 2 ? () => setShowClaim(false) : undefined}
      />
    );
  }

  async function handlePasskey() {
    setBusy(true);
    setError(null);
    try {
      await signInWithPasskey();
      await refresh();
    } catch (err) {
      setError(describePasskeyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="center">
      <div className="card">
        <h1>Duke Budget</h1>

        {!passkeysSupported ? (
          <p className="error">
            This browser does not support passkeys. Use the code option below, or open the site in
            Safari or Chrome.
          </p>
        ) : (
          <button className="primary big" onClick={handlePasskey} disabled={busy}>
            {busy ? 'Waiting for your device…' : 'Sign in'}
          </button>
        )}

        {error && <p className="error">{error}</p>}

        {fallback === 'none' && (
          <div className="links">
            <button className="link" onClick={() => setFallback('totp')}>
              Use a code instead
            </button>
            <button className="link" onClick={() => setFallback('recovery')}>
              Lost my phone
            </button>
          </div>
        )}

        {fallback !== 'none' && (
          <CodeFallback mode={fallback} onCancel={() => setFallback('none')} />
        )}

        {claim.open && fallback === 'none' && (
          <p className="hint setup-note">
            Setting up the second account?{' '}
            <button className="link" onClick={() => setShowClaim(true)}>
              Create it here
            </button>
          </p>
        )}
      </div>
    </main>
  );
}

function CodeFallback({ mode, onCancel }: { mode: 'totp' | 'recovery'; onCancel: () => void }) {
  const { refresh } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [userId, setUserId] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ users: User[] }>('/api/auth/users')
      .then((r) => {
        setUsers(r.users);
        setUserId(r.users[0]?.id ?? null);
      })
      .catch(() => setError('Could not load accounts.'));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (userId === null) return;
    setBusy(true);
    setError(null);
    try {
      const path = mode === 'totp' ? '/api/auth/totp' : '/api/auth/recovery';
      await api.post(path, { userId, code });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
      setBusy(false);
    }
  }

  return (
    <form className="fallback" onSubmit={submit}>
      <h2>{mode === 'totp' ? 'Authenticator code' : 'Recovery code'}</h2>

      <label>
        Who are you?
        <select value={userId ?? ''} onChange={(e) => setUserId(Number(e.target.value))}>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </select>
      </label>

      <label>
        {mode === 'totp' ? '6-digit code' : 'Recovery code'}
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode={mode === 'totp' ? 'numeric' : 'text'}
          autoComplete={mode === 'totp' ? 'one-time-code' : 'off'}
          placeholder={mode === 'totp' ? '123456' : 'ABCD-EFGH'}
          autoFocus
          required
        />
      </label>

      {mode === 'recovery' && (
        <p className="hint">
          Each recovery code works once. After signing in, register a passkey on this device.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      <div className="row">
        <button type="button" className="link" onClick={onCancel}>
          Back
        </button>
        <button type="submit" className="primary" disabled={busy || !code}>
          {busy ? 'Checking…' : 'Sign in'}
        </button>
      </div>
    </form>
  );
}
